import { useMemo } from "react"

import {
  useProductPositionPriceQuery,
  useSharedProductPositionPriceQuery
} from "shared/api/price/queries.generated"
import { exists } from "shared/lib/exists"
import { PriceGroupType } from "shared/types/Price"

import { useProductPositionFragment } from "entities/product-positions/api/useProductPositionFragment"
import { useProjectStore } from "features/project-management/model/useProjectStore"

export function useProductPositionPrice({
  id,
  projectID,
  quantity,
  isShareID = false
}: {
  id: string | null | undefined
  projectID: string
  quantity?: number
  isShareID?: boolean
}) {
  const productPosition = useProductPositionFragment(id)
  const { pendingElementIDs } = useProjectStore(projectID)
  const isPending = !!id && pendingElementIDs.includes(id)
  const {
    data: price,
    loading: isLoading,
    error
  } = useProductPositionPriceQuery({
    variables: { id: id as string },
    skip: !id || isPending || isShareID
  })

  const {
    data: sharedPrice,
    loading: isSharedPriceLoading,
    error: sharedPriceError
  } = useSharedProductPositionPriceQuery({
    variables: { projectShareID: projectID, productPositionID: id as string },
    skip: !id || isPending || !isShareID
  })

  const piecePrice = price?.productPositionPrice?.itemPrice

  const isSpecialPriceGroup = useMemo(
    () =>
      !!price?.productPositionPrice?.groups?.some(
        group => group.groupType === PriceGroupType.SPECIAL
      ),
    [price?.productPositionPrice?.groups]
  )

  return {
    price:
      price?.productPositionPrice ||
      sharedPrice?.sharedProductPositionPrice ||
      null,
    priceTotal: exists(piecePrice) ? piecePrice * (quantity || 1) : null,
    isLoading:
      isLoading ||
      isPending ||
      isSharedPriceLoading ||
      (!error &&
        !!price?.productPositionPrice &&
        !price?.productPositionPrice?.isValid),
    isOverwritten: exists(productPosition?.price),
    isPriceGroupOverwritten: exists(productPosition?.priceGroup),
    isSpecialPriceGroup,
    error: error || sharedPriceError
  }
}
